/**
 * Guarding "add another line" buttons.
 *
 * A blank row added on top of a blank row is not a new line, it is clutter —
 * and on a document that posts to the ledger it is a half-finished entry
 * waiting to be saved by accident. So the button waits until the row before it
 * actually says something.
 *
 * The check returns the *names* of what is missing rather than a bare boolean,
 * so the button can explain why it is disabled instead of just sitting there
 * dead.
 */

/**
 * Is one field filled in?
 *
 * Values arrive in several shapes on these forms: a plain string from an
 * input, a `{value,label}` object from react-select, or a bare id string from
 * the Select2 wrapper. A numeric field also has to be more than zero — "0" is
 * typed, but it is not an amount.
 */
const isFilled = (value, numeric) => {
  if (value === null || value === undefined) return false;
  if (typeof value === "object") return isFilled(value.value, numeric);
  if (numeric) return (parseFloat(value) || 0) > 0;
  return String(value).trim() !== "";
};

/**
 * What the last row still needs.
 *
 * @param {Array<object>} rows
 * @param {Array<{key: string, label: string, numeric?: boolean}>} required
 * @returns {string[]} labels of the missing fields; empty when the row is ready
 */
export const missingFromLastRow = (rows, required) => {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  const last = rows[rows.length - 1];
  if (!last) return [];
  return required
    .filter((field) => !isFilled(last[field.key], field.numeric))
    .map((field) => field.label);
};

/** Whether another row may be added yet. */
export const canAddRow = (rows, required) => missingFromLastRow(rows, required).length === 0;

/**
 * The tooltip for a blocked button — it names what is missing, so the fix is
 * obvious without hunting along the row.
 */
export const addRowBlockedReason = (rows, required, noun = "line") => {
  const missing = missingFromLastRow(rows, required);
  if (missing.length === 0) return "";
  const list = missing.length === 1
    ? missing[0]
    : `${missing.slice(0, -1).join(", ")} and ${missing[missing.length - 1]}`;
  return `Finish the current ${noun} first — ${list} still needed.`;
};

export default { missingFromLastRow, canAddRow, addRowBlockedReason };
